import FormImageCropper from 'components/form/FormImageCropper';
import FormSelect from 'components/form/FormSelect';
import FormText from 'components/form/FormText';
import FormWysiwygEditor from 'components/form/FormWysiwygEditor';
import IndicatorButton from 'components/indicator/IndicatorButton';
import config from 'config/appConfig';
import { AppConst } from 'constants/appConst';
import { DivConst } from 'constants/divConst';
import { AppHelper } from 'helpers/appHelper';
import { TermsHelper } from 'helpers/termsHelper';
import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import mime from 'mime';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import { useRecoilState } from 'recoil';
import { AcctState, AcctStateType } from 'stores/atoms/acctState';

/**
 *　カンパニー設定画面
 */
export default function SettingCompany(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  type FormStateType = {
    name: string;
    tel: string;
    zipCode: string;
    prefectureDiv: number;
    city: string;
    address: string;
    buildingName: string;
    reportMailSenderName: string;
    reportMailSenderAddress: string;
    reportMailReplyAddress: string;
    reportMailSignatureHtml: string;
    privacyPolicyUrl: string;
    copyright?: string;
    termsInputDiv: number;
    termsUrl?: string;
    termsText?: string;
    apiKeyBenchmark?: string;
    apiKeyGoogleAnalytics?: string;
  };

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();

  const [acctState] = useRecoilState(AcctState);

  const { updateAcctState, noticeMessageSuccess, noticeMessageFailure } = useAppState();
  const { postCompanyUpdate, postTermsPublish, postTermsPublishTerminate } = useApiState();

  const [initialized, setInitialized] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [isImageCropperOpenState, setIsImageCropperOpenState] = useState(false);
  const [isImageCropperResetState, setIsImageCropperResetState] = useState(false);
  const [imageBlobState, setImageBlobState] = useState<Blob>(null);
  const [processingOnPreview, setProcessingOnPreview] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm<FormStateType>({
    defaultValues: {
      termsInputDiv: DivConst.items.termsInputType.text.value,
    },
  });

  const termsInputDiv = watch('termsInputDiv');
  const isTermsInputDivUrl =
    termsInputDiv.toString() === DivConst.items.termsInputType.url.value.toString();
  const isTermsInputDivText =
    termsInputDiv.toString() === DivConst.items.termsInputType.text.value.toString();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    try {
      // フォームの初期化
      _initState(acctState);

      // 利用規約の入力タイプがテキストの場合、テキストが空の場合にサンプルテキストを設定
      const termsText = watch('termsText');
      if (!termsText) {
        setValue('termsText', AppConst.termSample);
      }
    } catch (e) {
      showBoundary(e);
    } finally {
      setInitialized(true);
    }
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　画像アップロードボタンクリック時の処理
   */
  const onImageUpload = () => {
    setIsImageCropperOpenState(true);
  };

  /**
   *　画像削除ボタンクリック時の処理
   */
  const onImageDelete = () => {
    setIsImageCropperResetState(true);
  };

  /**
   *　利用規約プレビューを開く
   */
  const onPreviewTerms = () =>
    AppHelper.submit(processingOnPreview, setProcessingOnPreview, async () => {
      try {
        await postTermsPublish({
          companyId: acctState.company.id,
          termsInputDiv: watch('termsInputDiv'),
          termsText: watch('termsText'),
          copyright: watch('copyright'),
          mode: 'preview',
        });
        TermsHelper.openPreviewUrl(acctState.company.cd);
      } catch (e) {
        noticeMessageFailure('利用規約のプレビューに失敗しました。');
        showBoundary(e);
      }
    });

  /**
   *　利用規約をリセットする
   */
  const onResetTerms = () => {
    setValue('termsText', AppConst.termSample);
  };

  /**
   *　保存ボタン押下時の処理
   */
  const onSave = () =>
    AppHelper.submit(
      processing,
      setProcessing,
      handleSubmit(async (data: FormStateType) => {
        try {
          await postCompanyUpdate({
            companyId: acctState.company.id,
            name: data.name,
            tel: data.tel,
            zipCode: data.zipCode,
            prefectureDiv: data.prefectureDiv,
            city: data.city,
            address: data.address,
            buildingName: data.buildingName,
            imageUploadKey: imageBlobState
              ? await AppHelper.uploadS3(imageBlobState, mime.getExtension(imageBlobState.type))
              : null,
            imageResetFlag: isImageCropperResetState,
            reportMailSenderName: data.reportMailSenderName,
            reportMailReplyAddress: data.reportMailReplyAddress,
            reportMailSignatureHtml: data.reportMailSignatureHtml,
            privacyPolicyUrl: data.privacyPolicyUrl,
            copyright: data.copyright,
            termsInputDiv: data.termsInputDiv,
            termsUrl: data.termsUrl,
            termsText: data.termsText,
            apiKeyBenchmark: data.apiKeyBenchmark,
            apiKeyGoogleAnalytics: data.apiKeyGoogleAnalytics,
          });

          // アカウント関連のステートを更新
          const newState = await updateAcctState(['user', 'company'], acctState.company.cd);

          // 画面のステートを初期化
          _initState(newState);

          if (isTermsInputDivText) {
            // 利用規約を公開
            await postTermsPublish({
              companyId: acctState.company.id,
              termsInputDiv: watch('termsInputDiv'),
              termsText: watch('termsText'),
              copyright: watch('copyright'),
              mode: 'production',
            });
          } else if (isTermsInputDivUrl) {
            // 利用規約を公開停止
            await postTermsPublishTerminate({
              companyId: acctState.company.id,
              termsInputDiv: watch('termsInputDiv'),
              mode: 'production',
            });
          }

          // 処理完了メッセージの表示
          noticeMessageSuccess('カンパニー情報の更新が完了しました。');

          return;
        } catch (e) {
          noticeMessageFailure('カンパニー情報の更新に失敗しました。');
          showBoundary(e);
        }
      })
    );

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　画面のステートを初期化します。
   */
  const _initState = (state: AcctStateType) => {
    // フォームの初期値設定
    setValue('name', state.company.name);
    setValue('tel', state.company.tel);
    setValue('zipCode', state.company.zipCode);
    setValue('prefectureDiv', state.company.prefectureDiv);
    setValue('city', state.company.city);
    setValue('address', state.company.address);
    setValue('buildingName', state.company.buildingName);
    setValue('reportMailSenderName', state.company.reportMailSenderName);
    setValue('reportMailSenderAddress', config.ses.from.address);
    setValue('reportMailReplyAddress', state.company.reportMailReplyAddress);
    setValue('reportMailSignatureHtml', state.company.reportMailSignatureHtml);
    setValue('privacyPolicyUrl', state.company.privacyPolicyUrl);
    setValue('copyright', state.company.copyright);
    setValue(
      'termsInputDiv',
      state.company.termsInputDiv || DivConst.items.termsInputType.text.value
    );
    setValue('termsUrl', state.company.termsUrl);
    setValue('termsText', state.company.termsText);
    setValue('apiKeyBenchmark', state.company.apiKeyBenchmark);
    setValue('apiKeyGoogleAnalytics', state.company.apiKeyGoogleAnalytics);

    setImageBlobState(null);
    setIsImageCropperOpenState(false);
    setIsImageCropperResetState(false);
  };

  /**
   *　管理権限を持っているかどうかを判定します。
   */
  const _isAdmin = () => {
    return acctState.user && acctState.company.member?.roleDiv === DivConst.items.role.admin.value;
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {initialized && (
        <>
          <section className="section-header">
            <div className="container">
              <div className="d-md-flex d-flex-header">
                <div className="flex-shrink-0 flex-left">
                  <h2 className="flex-title">基本設定</h2>
                </div>
              </div>
            </div>
          </section>
          <section className="section-basic-setting">
            <div className="container">
              <div className="box-common">
                <form className="form-profile g-3">
                  <div className="form-about-us">
                    <div className="d-md-flex d-flex-header">
                      <div className="flex-shrink-0 flex-left mb-3">
                        <h3 className="title-section title-md fw-bold">会社情報</h3>
                      </div>
                      {_isAdmin() && (
                        <div className="flex-grow-1 flex-right">
                          <ul className="list-inline list-btn mb-0">
                            <li className="list-inline-item">
                              <button
                                onClick={onSave}
                                type="button"
                                className="btn btn-common btn-common-w200 moderate-blue py-2 js-button-change"
                                disabled={processing}
                              >
                                <IndicatorButton visible={processing} />
                                変更する
                              </button>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="d-flex justify-content-start align-items-center">
                      <div className="flex-shrink-0 flex-img me-4">
                        <FormImageCropper
                          isOpen={isImageCropperOpenState}
                          setIsOpen={setIsImageCropperOpenState}
                          isReset={isImageCropperResetState}
                          setIsReset={setIsImageCropperResetState}
                          imageUrl={AppHelper.getCompanyImageUrl(acctState.company.imageKey)}
                          imageUrlDefault={AppHelper.getCompanyImageUrl(null)}
                          cropWidth={256}
                          cropRatio={1}
                          displayWidth={80}
                          setImageBlob={setImageBlobState}
                        />
                      </div>
                      {_isAdmin() && (
                        <div className="flex-grow-1 flex-text mb-0 ps-1">
                          <a onClick={onImageUpload} className="mb-2">
                            画像アップロード
                          </a>
                          <br />
                          <a onClick={onImageDelete}>削除</a>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="row form-row flex-md-row justify-content-between">
                      <div className="column col-lg-6 col-xl-6 col-left">
                        <FormText
                          subject="法人名"
                          name="name"
                          placeholder="株式会社サンプル"
                          register={register}
                          errors={errors}
                          required={true}
                          maxLength={100}
                          requiredMark={true}
                          readOnly={!_isAdmin()}
                        />
                      </div>
                      <div className="column col-lg-6 col-xl-6 col-right">
                        <FormText
                          subject="電話番号"
                          name="tel"
                          placeholder="1234567890"
                          register={register}
                          errors={errors}
                          required={true}
                          maxLength={50}
                          format="numeric"
                          requiredMark={true}
                          readOnly={!_isAdmin()}
                        />
                      </div>
                    </div>
                    <div className="row form-row flex-md-row justify-content-between">
                      <div className="column col-lg-6 col-xl-6 col-left">
                        <FormText
                          subject="郵便番号"
                          name="zipCode"
                          placeholder="1000001"
                          register={register}
                          errors={errors}
                          required={true}
                          maxLength={7}
                          minLength={7}
                          format="numeric"
                          requiredMark={true}
                          readOnly={!_isAdmin()}
                        />
                      </div>
                      <div className="column col-lg-6 col-xl-6 col-right">
                        <FormSelect
                          subject="都道府県"
                          name="prefectureDiv"
                          register={register}
                          errors={errors}
                          options={AppHelper.getDivSelectOptions('prefecture')}
                          required={true}
                          blank={true}
                          blankLabel="選択してください"
                          requiredMark={true}
                          disabled={!_isAdmin()}
                        />
                      </div>
                    </div>
                    <div className="row form-row flex-md-row justify-content-between">
                      <div className="column col-lg-6 col-xl-6 col-left">
                        <FormText
                          subject="市区町村"
                          name="city"
                          placeholder="千代田区"
                          register={register}
                          errors={errors}
                          required={true}
                          maxLength={100}
                          requiredMark={true}
                          readOnly={!_isAdmin()}
                        />
                      </div>
                      <div className="column col-lg-6 col-xl-6 col-right">
                        <FormText
                          subject="番地以降"
                          name="address"
                          placeholder="丸の内1-1-1"
                          register={register}
                          errors={errors}
                          required={true}
                          maxLength={100}
                          requiredMark={true}
                          readOnly={!_isAdmin()}
                        />
                      </div>
                    </div>
                    <div className="row form-row flex-md-row justify-content-between">
                      <div className="column col-lg-6 col-xl-6 col-left">
                        <FormText
                          subject="建物名・部屋番号"
                          name="buildingName"
                          placeholder="サンプルビル1F"
                          register={register}
                          errors={errors}
                          maxLength={100}
                          requiredMark={true}
                          readOnly={!_isAdmin()}
                        />
                      </div>
                      <div className="column col-lg-6 col-xl-6 col-right"></div>
                    </div>
                    <div className="row form-row flex-md-row justify-content-between">
                      <div className="column col-lg-6 col-xl-6 col-left">
                        <div className="form-label">参画中プラン</div>
                        <p className="ms-3 fw-bold mb-0">
                          {AppHelper.getDivLabel('planType', acctState.company.plan.planTypeDiv)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <h3 className="title-section title-md fw-bold">メール配信設定</h3>
                    <div className="row form-row flex-md-row justify-content-between">
                      <div className="column col-lg-6 col-xl-6 col-left">
                        <FormText
                          subject="差出人名"
                          name="reportMailSenderName"
                          placeholder="株式会社サンプル"
                          register={register}
                          errors={errors}
                          required={true}
                          maxLength={100}
                          requiredMark={true}
                          readOnly={!_isAdmin()}
                        />
                        <p className="mt-1">レポートメールの送信元名を変更できます</p>
                      </div>
                      <div className="column col-lg-6 col-xl-6 col-right">
                        <FormText
                          subject="送信元メールアドレス"
                          name="reportMailSenderAddress"
                          placeholder="no-reply@branq.jp"
                          register={register}
                          errors={errors}
                          maxLength={100}
                          requiredMark={true}
                          readOnly={true}
                        />
                        <p className="mt-1">現在のプランでは変更できません</p>
                      </div>
                    </div>
                    <div className="row form-row flex-md-row justify-content-between">
                      <div className="column col-lg-6 col-xl-6 col-left">
                        <FormText
                          subject="返信元メールアドレス"
                          name="reportMailReplyAddress"
                          placeholder="no-reply@branq.jp"
                          format="email"
                          register={register}
                          errors={errors}
                          required={true}
                          maxLength={300}
                          requiredMark={true}
                          readOnly={!_isAdmin()}
                        />
                        {/* <p className="mt-1">
                          レポートメールの返信元名を変更できます
                        </p> */}
                      </div>
                      <div className="column col-lg-6 col-xl-6 col-right"></div>
                    </div>
                    <div className="row form-row flex-md-row justify-content-between">
                      <div className="mt-4 col-left">
                        <FormWysiwygEditor
                          subject="レポートメール末尾"
                          name="reportMailSignatureHtml"
                          placeholder="メール署名部テキスト"
                          errors={errors}
                          control={control}
                          required={true}
                          toolbar={['link']}
                          requiredMark={true}
                          readOnly={!_isAdmin()}
                        />
                        <p className="mt-1">レポートメール末尾の署名部分のテキストです</p>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="row form-row flex-md-row justify-content-between">
                      <div className="column col-lg-6 col-xl-6 col-left">
                        <FormText
                          subject="プライバシーポリシーのURL"
                          name="privacyPolicyUrl"
                          placeholder="http://example.com/plivacypolicy"
                          register={register}
                          errors={errors}
                          maxLength={100}
                          requiredMark={true}
                          format="url"
                          required={true}
                          readOnly={!_isAdmin()}
                        />
                        <p className="mt-1">
                          プライバシーポリシーが記載されたページのURLを記入してください
                        </p>
                      </div>
                      <div className="column col-lg-6 col-xl-6 col-right">
                        <FormText
                          subject="コピーライトの設定"
                          name="copyright"
                          placeholder="©株式会社サンプル"
                          register={register}
                          errors={errors}
                          maxLength={100}
                          required={true}
                          requiredMark={true}
                          readOnly={!_isAdmin()}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <h3 className="title-section title-md fw-bold">利用規約*</h3>
                    <div className="row form-row flex-md-row justify-content-between mb-3">
                      <div className="mt-4 col-left">
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="termsInputDiv"
                            id="termsInputDiv1"
                            value={DivConst.items.termsInputType.url.value}
                            {...register('termsInputDiv', { valueAsNumber: true })}
                            checked={isTermsInputDivUrl}
                          />
                          <label className="form-check-label" htmlFor="termsInputDivUrl">
                            URLで指定
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="termsInputDiv"
                            id="termsInputDiv2"
                            value={DivConst.items.termsInputType.text.value}
                            {...register('termsInputDiv', { valueAsNumber: true })}
                            checked={isTermsInputDivText}
                          />
                          <label className="form-check-label" htmlFor="termsInputDivText">
                            テキストで指定
                          </label>
                        </div>
                        <p className="mt-1">
                          利用規約が記載されたページのURLまたは利用規約のテキストを入力してください
                        </p>
                      </div>
                    </div>
                    <div className="row form-row flex-md-row justify-content-between">
                      <div className="column col-lg-6 col-xl-6 col-left">
                        <FormText
                          subject="利用規約のURL"
                          name="termsUrl"
                          placeholder="http://example.com/terms"
                          register={register}
                          errors={errors}
                          maxLength={100}
                          format={isTermsInputDivUrl ? 'url' : undefined}
                          readOnly={!_isAdmin()}
                          required={isTermsInputDivUrl}
                          disabled={isTermsInputDivText}
                        />
                      </div>
                    </div>
                    <div className="row form-row flex-md-row justify-content-between">
                      <div className="mt-4 col-left">
                        <FormWysiwygEditor
                          subject="利用規約のテキスト"
                          name="termsText"
                          placeholder="利用規約のテキスト"
                          errors={errors}
                          control={control}
                          toolbar={['link']}
                          readOnly={!_isAdmin()}
                          required={isTermsInputDivText}
                          disabled={isTermsInputDivUrl}
                        />
                      </div>
                      <div className="d-flex gap-3">
                        <a
                          onClick={onPreviewTerms}
                          className={`mb-2 ${isTermsInputDivUrl ? 'text-muted' : ''}`}
                          style={{
                            cursor: isTermsInputDivUrl ? 'not-allowed' : 'pointer',
                            pointerEvents: isTermsInputDivUrl ? 'none' : 'auto',
                          }}
                        >
                          <IndicatorButton visible={processingOnPreview} />
                          プレビュー
                        </a>
                        <a
                          onClick={onResetTerms}
                          className={`mb-2 ${isTermsInputDivUrl ? 'text-muted' : ''}`}
                          style={{
                            cursor: isTermsInputDivUrl ? 'not-allowed' : 'pointer',
                            pointerEvents: isTermsInputDivUrl ? 'none' : 'auto',
                          }}
                        >
                          リセット
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <h3 className="title-section title-md fw-bold">連携</h3>
                    <div className="row form-row flex-md-row justify-content-between">
                      <div className="column col-lg-6 col-xl-6 col-left">
                        <FormText
                          subject="Benchmark API KEY"
                          name="apiKeyBenchmark"
                          placeholder="APIキー"
                          register={register}
                          errors={errors}
                          maxLength={100}
                          requiredMark={true}
                          readOnly={!_isAdmin()}
                        />
                        <p className="mt-1">Benchmarkとの連携ができます</p>
                      </div>
                      <div className="column col-lg-6 col-xl-6 col-right">
                        <FormText
                          subject="Google Analytics ID"
                          name="apiKeyGoogleAnalytics"
                          placeholder="UA-XXXXX-Y"
                          register={register}
                          errors={errors}
                          maxLength={100}
                          requiredMark={true}
                          readOnly={!_isAdmin()}
                        />
                        <p className="mt-1">Google Analyticsとの連携ができます</p>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
}
